// ============================================================================
// SESSION TASKS TAB
// Table-based layout mirroring ProjectTasksTab: sortable, searchable,
// paginated, multi-select with bulk cancel.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Plus, CheckCircle, XCircle, Clock, Eye, RotateCcw, FileInput,
  AlertTriangle, ClipboardList, Ban, Loader2, Zap,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Search, ArrowUpDown, Bookmark
} from 'lucide-react';
import {
  SessionTaskSummary,
  TaskCategory,
  TaskStatus,
  SessionTaskContext
} from '../../types/session-task.types';
import sessionTasksService from '../../services/sessionTasksService';
import { CreateSessionTaskModal } from './CreateSessionTaskModal';

interface SessionTasksTabProps {
  sessionId: string;
  projectId?: string;
  sessionContext: SessionTaskContext;
  designContext?: string;
  onTaskSelect?: (workflowId: string, executionId: string) => void;
}

const PAGE_SIZE = 25;
type SortField = 'name' | 'status' | 'category' | 'created' | 'duration';

const SortIcon: React.FC<{ isActive: boolean; direction: 'asc' | 'desc' }> = ({ isActive, direction }) => {
  if (!isActive) return <ArrowUpDown size={12} className="text-slate-300" />;
  return direction === 'asc'
    ? <ChevronUp size={12} className="text-primary" />
    : <ChevronDown size={12} className="text-primary" />;
};

const ACTIVE_STATUSES = ['running', 'waiting_human', 'pending', 'not_started'];

export const SessionTasksTab: React.FC<SessionTasksTabProps> = ({
  sessionId,
  projectId,
  sessionContext,
  designContext,
  onTaskSelect
}) => {
  const [tasks, setTasks] = useState<SessionTaskSummary[]>([]);
  const [taskStats, setTaskStats] = useState({ active: 0, completed: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<TaskCategory | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<TaskStatus | 'all'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Table state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('created');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [bulkLoading, setBulkLoading] = useState(false);

  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const loadStats = useCallback(async () => {
    try {
      const allTasks = await sessionTasksService.listTasks(sessionId, {});
      setTaskStats({
        active: allTasks.filter(t => ACTIVE_STATUSES.includes(t.status)).length,
        completed: allTasks.filter(t => t.status === 'completed').length,
        failed: allTasks.filter(t => t.status === 'failed').length,
      });
    } catch { /* best-effort */ }
  }, [sessionId]);

  const loadTasks = useCallback(async () => {
    setError(null);
    try {
      const filters: any = {};
      if (filterCategory !== 'all') filters.category = [filterCategory];
      if (filterStatus !== 'all') filters.status = [filterStatus];
      const loadedTasks = await sessionTasksService.listTasks(sessionId, filters);
      setTasks(loadedTasks);
    } catch (err: any) {
      setError('Failed to load tasks: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId, filterCategory, filterStatus]);

  // Show loading spinner when filters/session change
  useEffect(() => {
    setLoading(true);
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [sessionId, filterCategory, filterStatus]);

  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // Poll while there are active tasks
  useEffect(() => {
    const hasActive = tasks.some(t => ACTIVE_STATUSES.includes(t.status));
    if (!hasActive) return;
    const interval = setInterval(() => { loadTasks(); loadStats(); }, 15000);
    return () => clearInterval(interval);
  }, [tasks, loadTasks, loadStats]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [searchQuery]);

  const handleTaskCreated = (workflowId: string, executionId: string) => {
    setShowCreateModal(false);
    loadTasks();
    loadStats();
    onTaskSelect?.(workflowId, executionId);
  };

  const handleRerunTask = async (workflowId: string) => {
    try {
      await sessionTasksService.rerunTask(sessionId, workflowId);
      loadTasks();
      loadStats();
    } catch (err: any) {
      alert('Failed to rerun task: ' + err.message);
    }
  };

  const handleCancelTask = async (workflowId: string, executionId: string) => {
    try {
      await sessionTasksService.cancelTask(sessionId, workflowId, executionId);
      loadTasks();
      loadStats();
    } catch (err: any) {
      alert('Failed to cancel task: ' + err.message);
    }
  };

  const handleAddToContext = async (workflowId: string) => {
    try {
      const result = await sessionTasksService.addTaskToContext(sessionId, workflowId);
      alert(`Added ${result.addedCount} outputs to session context`);
    } catch (err: any) {
      alert('Failed to add to context: ' + err.message);
    }
  };

  const handleToggleTaggedForExport = async (workflowId: string, current: boolean) => {
    const next = !current;
    setTasks(prev => prev.map(t => t.workflowId === workflowId ? { ...t, taggedForExport: next } : t));
    try {
      await sessionTasksService.setTaggedForExport(sessionId, workflowId, next);
    } catch {
      setTasks(prev => prev.map(t => t.workflowId === workflowId ? { ...t, taggedForExport: current } : t));
    }
  };

  const handleBulkCancel = async () => {
    const cancelable = Array.from(selectedIds).filter(id => {
      const t = tasks.find(t => t.workflowId === id);
      return t && ACTIVE_STATUSES.includes(t.status);
    });
    if (cancelable.length === 0) return;
    if (!window.confirm(`Cancel ${cancelable.length} running task${cancelable.length !== 1 ? 's' : ''}?`)) return;

    setBulkLoading(true);
    try {
      await Promise.all(
        cancelable.map(id => {
          const t = tasks.find(t => t.workflowId === id)!;
          return sessionTasksService.cancelTask(sessionId, id, t.executionId || id);
        })
      );
      setSelectedIds(new Set());
      loadTasks();
      loadStats();
    } catch (err: any) {
      alert('Bulk cancel failed: ' + err.message);
    } finally {
      setBulkLoading(false);
    }
  };

  const isTaskStale = (task: SessionTaskSummary): boolean => {
    if (task.status !== 'running' || !task.startedAt) return false;
    return Date.now() - new Date(task.startedAt).getTime() > 10 * 60 * 1000;
  };

  const formatDuration = (startedAt: string, completedAt?: string) => {
    const ms = (completedAt ? new Date(completedAt).getTime() : Date.now()) - new Date(startedAt).getTime();
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const getDurationMs = (task: SessionTaskSummary): number => {
    if (!task.startedAt) return 0;
    return (task.completedAt ? new Date(task.completedAt).getTime() : Date.now()) - new Date(task.startedAt).getTime();
  };

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  // Search + sort + paginate
  const filteredTasks = useMemo(() => {
    if (!searchQuery.trim()) return tasks;
    const q = searchQuery.toLowerCase();
    return tasks.filter(t => t.taskName.toLowerCase().includes(q));
  }, [tasks, searchQuery]);

  const sortedTasks = useMemo(() => {
    const sorted = [...filteredTasks];
    const dir = sortDirection === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      switch (sortField) {
        case 'name': return dir * a.taskName.localeCompare(b.taskName);
        case 'status': return dir * a.status.localeCompare(b.status);
        case 'category': return dir * a.taskCategory.localeCompare(b.taskCategory);
        case 'created': return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        case 'duration': return dir * (getDurationMs(a) - getDurationMs(b));
        default: return 0;
      }
    });
    return sorted;
  }, [filteredTasks, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedTasks.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedTasks = sortedTasks.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const rangeStart = sortedTasks.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, sortedTasks.length);

  // Checkbox header indeterminate state
  const pageIds = paginatedTasks.map(t => t.workflowId);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
  const somePageSelected = pageIds.some(id => selectedIds.has(id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds(prev => { const next = new Set(prev); pageIds.forEach(id => next.delete(id)); return next; });
    } else {
      setSelectedIds(prev => { const next = new Set(prev); pageIds.forEach(id => next.add(id)); return next; });
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'created' ? 'desc' : 'asc');
    }
  };

  const statusDot = (status: string) => {
    switch (status) {
      case 'running':
        return (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
        );
      case 'waiting_human':
        return <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" /></span>;
      case 'completed':
        return <span className="inline-flex rounded-full h-2 w-2 bg-green-500" />;
      case 'failed':
        return <span className="inline-flex rounded-full h-2 w-2 bg-red-500" />;
      case 'cancelled':
        return <span className="inline-flex rounded-full h-2 w-2 bg-amber-400" />;
      default:
        return <span className="inline-flex rounded-full h-2 w-2 bg-slate-300" />;
    }
  };

  const statusLabelColor = (status: string) => {
    switch (status) {
      case 'running': return 'text-primary';
      case 'waiting_human': return 'text-amber-600';
      case 'completed': return 'text-green-600';
      case 'failed': return 'text-red-500';
      case 'cancelled': return 'text-amber-600';
      default: return 'text-slate-500';
    }
  };

  const statusLabelText = (status: string) => {
    switch (status) {
      case 'waiting_human': return 'Waiting';
      case 'not_started': return 'Pending';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const selectedCancelableCount = Array.from(selectedIds).filter(id =>
    tasks.find(t => t.workflowId === id && ACTIVE_STATUSES.includes(t.status))
  ).length;

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="max-w-[1100px] mx-auto flex flex-col gap-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex flex-col gap-2 rounded-xl p-5 bg-white border border-slate-200 shadow-sm relative overflow-hidden">
            <div className="absolute right-0 top-0 h-full w-1 bg-blue-500 rounded-r-xl" />
            <div className="flex justify-between items-start">
              <div>
                <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">Active Tasks</p>
                <p className="text-navy text-3xl font-bold mt-1">{taskStats.active}</p>
              </div>
              <div className="size-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-500">
                <Zap size={20} />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 rounded-xl p-5 bg-white border border-slate-200 shadow-sm relative overflow-hidden">
            <div className="absolute right-0 top-0 h-full w-1 bg-green-500 rounded-r-xl" />
            <div className="flex justify-between items-start">
              <div>
                <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">Completed</p>
                <p className="text-navy text-3xl font-bold mt-1">{taskStats.completed}</p>
              </div>
              <div className="size-10 rounded-full bg-green-50 flex items-center justify-center text-green-500">
                <CheckCircle size={20} />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 rounded-xl p-5 bg-white border border-slate-200 shadow-sm relative overflow-hidden">
            <div className="absolute right-0 top-0 h-full w-1 bg-red-500 rounded-r-xl" />
            <div className="flex justify-between items-start">
              <div>
                <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">Failed</p>
                <p className="text-navy text-3xl font-bold mt-1">{taskStats.failed}</p>
              </div>
              <div className="size-10 rounded-full bg-red-50 flex items-center justify-center text-red-500">
                <XCircle size={20} />
              </div>
            </div>
          </div>
        </div>

        {/* Filters + Search + Create */}
        <div className="bg-white rounded-xl border border-slate-200 p-2 shadow-sm flex flex-col md:flex-row justify-between items-center gap-3">
          <div className="flex w-full md:w-auto items-center gap-3">
            <div className="flex gap-1 p-1 bg-slate-50 rounded-lg">
              {(['all', 'thinking', 'doing'] as const).map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilterCategory(cat)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                    filterCategory === cat
                      ? 'bg-white shadow-sm font-bold text-navy'
                      : 'hover:bg-white/50 text-slate-500'
                  }`}
                >
                  {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">Status:</span>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as TaskStatus | 'all')}
                className="text-sm font-medium text-navy border-none bg-transparent cursor-pointer focus:outline-none"
              >
                <option value="all">Any</option>
                <option value="running">Running</option>
                <option value="waiting_human">Waiting Human</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-56">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search tasks..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
              />
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-1.5 text-sm font-bold text-white shadow-sm shadow-primary/20 hover:bg-primary-dark transition-colors whitespace-nowrap"
            >
              <Plus size={16} />
              Create Task
            </button>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl px-4 py-2.5">
            <span className="text-sm font-bold text-navy">{selectedIds.size} selected</span>
            <div className="h-4 w-px bg-slate-300" />
            {selectedCancelableCount > 0 && (
              <button
                onClick={handleBulkCancel}
                disabled={bulkLoading}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors disabled:opacity-50"
              >
                <Ban size={12} />
                Cancel {selectedCancelableCount} running
              </button>
            )}
            <button
              onClick={() => setSelectedIds(new Set())}
              className="ml-auto text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl border border-red-200 text-sm">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && tasks.length === 0 && (
          <div className="rounded-xl bg-white p-12 shadow-sm border border-slate-200 text-center">
            <Loader2 size={32} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-slate-500 text-sm font-medium">Loading tasks...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && tasks.length === 0 && (
          <div className="rounded-xl bg-white p-12 shadow-sm border border-slate-200 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <ClipboardList size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">No tasks yet. Create your first task to get started!</p>
          </div>
        )}

        {/* Tasks Table */}
        {!loading && tasks.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="w-10 px-3 py-3">
                      <input
                        ref={headerCheckboxRef}
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={toggleSelectAll}
                        className="rounded border-slate-300 text-primary focus:ring-primary/30"
                      />
                    </th>
                    <th className="px-3 py-3 text-left">
                      <button onClick={() => handleSort('status')} className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700">
                        Status <SortIcon isActive={sortField === 'status'} direction={sortDirection} />
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <button onClick={() => handleSort('name')} className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700">
                        Name <SortIcon isActive={sortField === 'name'} direction={sortDirection} />
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <button onClick={() => handleSort('category')} className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700">
                        Category <SortIcon isActive={sortField === 'category'} direction={sortDirection} />
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <button onClick={() => handleSort('created')} className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700">
                        Created <SortIcon isActive={sortField === 'created'} direction={sortDirection} />
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <button onClick={() => handleSort('duration')} className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700">
                        Duration <SortIcon isActive={sortField === 'duration'} direction={sortDirection} />
                      </button>
                    </th>
                    <th className="px-3 py-3 text-right text-xs uppercase tracking-wider font-semibold text-slate-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTasks.map(task => (
                    <tr
                      key={task.workflowId}
                      className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                        onTaskSelect && task.executionId ? 'cursor-pointer' : ''
                      } ${selectedIds.has(task.workflowId) ? 'bg-primary/5' : ''}`}
                      onClick={() => task.executionId && onTaskSelect?.(task.workflowId, task.executionId)}
                    >
                      <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(task.workflowId)}
                          onChange={() => toggleSelect(task.workflowId)}
                          className="rounded border-slate-300 text-primary focus:ring-primary/30"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <span className={`flex items-center gap-1.5 text-sm font-medium ${statusLabelColor(task.status)}`}>
                          {statusDot(task.status)}
                          <span>{statusLabelText(task.status)}</span>
                          {isTaskStale(task) && <AlertTriangle size={12} className="text-red-500 ml-0.5" />}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-sm font-medium text-navy hover:text-primary transition-colors truncate block max-w-[300px]">
                          {task.taskName}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className="inline-flex items-center gap-1.5">
                          {task.templateId?.includes('graph-sync') && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs font-semibold rounded-md border border-slate-200">System</span>
                          )}
                          {task.taskCategory === 'thinking'
                            ? <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-md">Thinking</span>
                            : <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-md">Doing</span>
                          }
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-sm text-slate-500">{formatTimeAgo(task.createdAt)}</span>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-sm text-slate-500">
                          {task.startedAt ? formatDuration(task.startedAt, task.completedAt) : '—'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {task.executionId && onTaskSelect && (
                            <button
                              onClick={() => onTaskSelect(task.workflowId, task.executionId!)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors"
                              title="View results"
                            >
                              <Eye size={14} />
                            </button>
                          )}
                          {task.status === 'completed' && (
                            <button
                              onClick={() => handleAddToContext(task.workflowId)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                              title="Add to context"
                            >
                              <FileInput size={14} />
                            </button>
                          )}
                          {task.status === 'completed' && (
                            <button
                              onClick={() => handleToggleTaggedForExport(task.workflowId, !!task.taggedForExport)}
                              title={task.taggedForExport ? 'Remove from export' : 'Tag for export'}
                              className={`p-1.5 rounded-md transition-colors ${
                                task.taggedForExport
                                  ? 'text-primary bg-primary/10 hover:bg-primary/20'
                                  : 'text-slate-400 hover:text-primary hover:bg-primary/5'
                              }`}
                            >
                              <Bookmark size={14} />
                            </button>
                          )}
                          {(task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled') && (
                            <button
                              onClick={() => handleRerunTask(task.workflowId)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors"
                              title="Rerun"
                            >
                              <RotateCcw size={14} />
                            </button>
                          )}
                          {ACTIVE_STATUSES.includes(task.status) && (
                            <button
                              onClick={() => handleCancelTask(task.workflowId, task.executionId || task.workflowId)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                              title={task.status === 'waiting_human' ? 'Review' : 'Cancel'}
                            >
                              {task.status === 'waiting_human' ? <Clock size={14} /> : <Ban size={14} />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50">
              <span className="text-xs text-slate-500">
                Showing {rangeStart}–{rangeEnd} of {sortedTasks.length} task{sortedTasks.length !== 1 ? 's' : ''}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={14} /> Previous
                </button>
                <span className="px-2 text-xs text-slate-500">
                  Page {safePage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* No search results */}
        {!loading && tasks.length > 0 && sortedTasks.length === 0 && searchQuery.trim() && (
          <div className="rounded-xl bg-white p-8 shadow-sm border border-slate-200 text-center">
            <Search size={24} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No tasks match "{searchQuery}"</p>
          </div>
        )}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <CreateSessionTaskModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          sessionId={sessionId}
          projectId={projectId}
          sessionContext={sessionContext}
          designContext={designContext}
          onSuccess={handleTaskCreated}
        />
      )}
    </div>
  );
};
